/**
 * AdForm Component
 * Form for creating and editing advertisements with image upload
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageUpload } from '../common/ImageUpload';
import type { ImageUploadResponse } from '../../services/imagesApi';
import './AdForm.css';

interface AdFormProps {
  initialData?: {
    title?: string;
    description?: string;
    category?: string;
    region?: string;
    district?: string;
    storeName?: string;
    phone?: string;
    address?: string;
    priceRange?: string;
    imageUrls?: string[];
    tags?: string[];
  };
  onSubmit?: (data: AdFormData) => Promise<void>;
  onCancel?: () => void;
  isEditing?: boolean;
  isLoading?: boolean;
  error?: string;
}

export interface AdFormData {
  title: string;
  description?: string;
  category: string;
  region: string;
  district?: string;
  storeName: string;
  phone?: string;
  address?: string;
  priceRange?: string;
  imageUrls: string[];
  tags: string[];
}

const CATEGORIES = ['維修', '保養', '美容', '改裝'] as const;
const REGIONS = ['台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市'] as const;
const PRICE_RANGES = ['$', '$$', '$$$', '$$$$'] as const;

export const AdForm: React.FC<AdFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isEditing = false,
  isLoading = false,
  error: externalError,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AdFormData>({
    title: initialData?.title || '',
    description: initialData?.description || '',
    category: initialData?.category || CATEGORIES[0],
    region: initialData?.region || REGIONS[0],
    district: initialData?.district || '',
    storeName: initialData?.storeName || '',
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    priceRange: initialData?.priceRange || '',
    imageUrls: initialData?.imageUrls || [],
    tags: initialData?.tags || [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        description: initialData.description || '',
        category: initialData.category || CATEGORIES[0],
        region: initialData.region || REGIONS[0],
        district: initialData.district || '',
        storeName: initialData.storeName || '',
        phone: initialData.phone || '',
        address: initialData.address || '',
        priceRange: initialData.priceRange || '',
        imageUrls: initialData.imageUrls || [],
        tags: initialData.tags || [],
      });
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleImagesUploaded = (responses: ImageUploadResponse[]) => {
    const newUrls = responses.map((r) => r.url);
    setFormData((prev) => ({
      ...prev,
      imageUrls: [...prev.imageUrls, ...newUrls],
    }));
  };

  const handleImageDelete = (urlToDelete: string) => {
    setFormData((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((url) => url !== urlToDelete),
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = '標題是必填欄位';
    }

    if (!formData.storeName.trim()) {
      newErrors.storeName = '商店名稱是必填欄位';
    }

    if (formData.phone && !/^[\d+()\-\\s]+$/.test(formData.phone)) {
      newErrors.phone = '請輸入有效的電話號碼';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit?.(formData);
    } catch (err) {
      console.error('Failed to submit form:', err);
      setErrors({ form: '提交失敗，請稍後再試' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/ads');
    }
  };

  return (
    <div className="ad-form">
      {/* Header */}
      <div className="ad-form__header">
        <h2 className="ad-form__title">{isEditing ? '編輯廣告' : '發布廣告'}</h2>
        <p className="ad-form__subtitle">填寫廣告資訊，讓更多客戶找到您</p>
      </div>

      <form onSubmit={handleSubmit} className="ad-form__form">
        {externalError && (
          <div className="ad-form__error">{externalError}</div>
        )}
        {errors.form && (
          <div className="ad-form__error">{errors.form}</div>
        )}

        {/* Basic Information */}
        <div className="ad-form__section">
          <h3 className="ad-form__section-title">基本資訊</h3>

          <div className="ad-form__field">
            <label className="ad-form__label" htmlFor="title">
              標題 <span className="ad-form__required">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`ad-form__input ${errors.title ? 'ad-form__input--error' : ''}`}
              placeholder="請輸入廣告標題"
              disabled={isSubmitting || isLoading}
              maxLength={100}
            />
            {errors.title && <span className="ad-form__error-text">{errors.title}</span>}
          </div>

          <div className="ad-form__field">
            <label className="ad-form__label" htmlFor="description">
              描述
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="ad-form__textarea"
              placeholder="請輸入廣告描述"
              disabled={isSubmitting || isLoading}
              rows={4}
              maxLength={500}
            />
          </div>

          <div className="ad-form__field-group">
            <div className="ad-form__field">
              <label className="ad-form__label" htmlFor="category">
                類別 <span className="ad-form__required">*</span>
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="ad-form__select"
                disabled={isSubmitting || isLoading}
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="ad-form__field">
              <label className="ad-form__label" htmlFor="region">
                地區 <span className="ad-form__required">*</span>
              </label>
              <select
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                className="ad-form__select"
                disabled={isSubmitting || isLoading}
              >
                {REGIONS.map((region) => (
                  <option key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="ad-form__field">
            <label className="ad-form__label" htmlFor="district">
              行政區
            </label>
            <input
              type="text"
              id="district"
              name="district"
              value={formData.district}
              onChange={handleChange}
              className="ad-form__input"
              placeholder="例如：信義區"
              disabled={isSubmitting || isLoading}
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="ad-form__section">
          <h3 className="ad-form__section-title">聯絡資訊</h3>

          <div className="ad-form__field">
            <label className="ad-form__label" htmlFor="storeName">
              商店名稱 <span className="ad-form__required">*</span>
            </label>
            <input
              type="text"
              id="storeName"
              name="storeName"
              value={formData.storeName}
              onChange={handleChange}
              className={`ad-form__input ${errors.storeName ? 'ad-form__input--error' : ''}`}
              placeholder="請輸入商店名稱"
              disabled={isSubmitting || isLoading}
            />
            {errors.storeName && <span className="ad-form__error-text">{errors.storeName}</span>}
          </div>

          <div className="ad-form__field-group">
            <div className="ad-form__field">
              <label className="ad-form__label" htmlFor="phone">
                電話
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`ad-form__input ${errors.phone ? 'ad-form__input--error' : ''}`}
                placeholder="02-1234-5678"
                disabled={isSubmitting || isLoading}
              />
              {errors.phone && <span className="ad-form__error-text">{errors.phone}</span>}
            </div>

            <div className="ad-form__field">
              <label className="ad-form__label" htmlFor="priceRange">
                價格範圍
              </label>
              <select
                id="priceRange"
                name="priceRange"
                value={formData.priceRange}
                onChange={handleChange}
                className="ad-form__select"
                disabled={isSubmitting || isLoading}
              >
                <option value="">選擇價格範圍</option>
                {PRICE_RANGES.map((range) => (
                  <option key={range} value={range}>
                    {range}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="ad-form__field">
            <label className="ad-form__label" htmlFor="address">
              地址
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="ad-form__input"
              placeholder="請輸入地址"
              disabled={isSubmitting || isLoading}
            />
          </div>
        </div>

        {/* Images */}
        <div className="ad-form__section">
          <h3 className="ad-form__section-title">圖片</h3>
          <ImageUpload
            entityType="ad"
            entityId="new"
            onUploadComplete={handleImagesUploaded}
            maxImages={10}
            currentImageCount={formData.imageUrls.length}
            existingImages={formData.imageUrls}
            onImageDelete={handleImageDelete}
          />
        </div>

        {/* Tags */}
        <div className="ad-form__section">
          <h3 className="ad-form__section-title">標籤</h3>

          <div className="ad-form__field">
            <label className="ad-form__label" htmlFor="tagInput">
              新增標籤
            </label>
            <div className="ad-form__tag-input-group">
              <input
                type="text"
                id="tagInput"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                className="ad-form__input"
                placeholder="輸入標籤後按 Enter 或點擊新增"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                disabled={isSubmitting || isLoading}
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="ad-form__button ad-form__button--secondary"
                disabled={isSubmitting || isLoading}
              >
                新增
              </button>
            </div>
          </div>

          {formData.tags.length > 0 && (
            <div className="ad-form__tags">
              {formData.tags.map((tag) => (
                <span key={tag} className="ad-form__tag">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ad-form__tag-remove"
                    disabled={isSubmitting || isLoading}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="ad-form__actions">
          <button
            type="button"
            onClick={handleCancel}
            className="ad-form__cancel"
            disabled={isSubmitting || isLoading}
          >
            取消
          </button>
          <button
            type="submit"
            className="ad-form__submit"
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? '提交中...' : isEditing ? '更新廣告' : '發布廣告'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdForm;
